// The decisions behind the training server calls. Pure functions, no Firebase,
// so the unit tests cover every rule. The Cloud Functions in functions/ read
// and write Firestore and call these.
// Words: TRAINING_SERVER_WORDS.md and TRAINEE_QUIZ_WORDS.md (approved 2026-10-05).

import {
  NODES,
  isCurrentOn,
  isPassed,
  nodeById,
  nextOpenNode,
  scorePct,
  type AnswerAsSeen,
  type NodeId,
  type Quiz,
  type QuizId,
  type QuizQuestion,
} from "./training";

// ---- Dates ----

/** Site time. All five sites today are on Eastern time, same as the live app. */
export const SITE_TIME_ZONE = "America/New_York";

/** YYYY-MM-DD in site time. */
export function dateInSiteTime(now: Date): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: SITE_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
  return parts;
}

/** "1:42 PM" in site time. Blank for a bad date. */
export function timeInSiteTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone: SITE_TIME_ZONE, hour: "numeric", minute: "2-digit" }).format(d);
}

/** The draft's start for the QEW page: "1:42 PM" today, "10/08 1:42 PM" on an earlier day. */
export function startedText(iso: string, now: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "earlier";
  const day = dateInSiteTime(d);
  const t = timeInSiteTime(iso);
  return day === dateInSiteTime(now) ? t : `${day.slice(5, 7)}/${day.slice(8, 10)} ${t}`;
}

// ---- IDs ----

export type IdKind = "SES" | "QA" | "TR" | "JSP" | "INS" | "WR" | "OBS" | "CV";

/** SES-0020, QA-0012, TR-0205, INS-0001. Four digits, more when the count passes 9999. */
export function formatId(kind: IdKind, n: number): string {
  return `${kind}-${String(n).padStart(4, "0")}`;
}

/** The number in an ID of this kind, or null. "TR-0202" -> 202. */
export function idNumber(kind: IdKind, id: string): number | null {
  const m = new RegExp(`^${kind}-(\\d+)$`).exec(id.trim());
  return m?.[1] ? Number(m[1]) : null;
}

/** Highest number found among IDs of this kind. 0 when none. */
export function highestId(kind: IdKind, ids: readonly string[]): number {
  let max = 0;
  for (const id of ids) {
    const n = idNumber(kind, id);
    if (n !== null && n > max) max = n;
  }
  return max;
}

/** No 0, O, 1, I or L, so a code read off a projector is never misread. */
export const CODE_LETTERS = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/** Four characters. `rand` returns 0 to 1, injected so tests are fixed. */
export function sessionCode(rand: () => number): string {
  let code = "";
  for (let i = 0; i < 4; i++) {
    code += CODE_LETTERS[Math.floor(rand() * CODE_LETTERS.length) % CODE_LETTERS.length];
  }
  return code;
}

// ---- Tags and quizzes ----

/** Typed tag to stored form, same as the live page. "01qew1" -> "01-QEW01". */
export function normalizeTag(raw: string): string {
  const t = raw.trim().toUpperCase().replace(/\s/g, "");
  const m = /^(\d{2})-?QEW-?(\d{1,3})$/.exec(t);
  if (!m?.[1] || !m[2]) return t;
  return `${m[1]}-QEW${m[2].padStart(2, "0")}`;
}

/** The tag's first two digits are the site. */
export function siteFromTag(tagId: string): string | null {
  return /^(\d{2})-/.exec(tagId)?.[1] ?? null;
}

/** QR value to quiz. "2" and "quiz2" both read as quiz2. */
export function quizIdFromParam(param: unknown): QuizId | null {
  const s = String(param ?? "").trim().toLowerCase();
  const n = s.startsWith("quiz") ? s.slice(4) : s;
  return n === "1" || n === "2" || n === "3" ? (`quiz${n}` as QuizId) : null;
}

/** Nodes with a record that has not expired. An expired node is open again. */
export function currentNodes(
  records: readonly { nodeId: NodeId; expiresOn: string | null }[],
  today: string,
): NodeId[] {
  const done = new Set<NodeId>();
  for (const r of records) if (isCurrentOn(r.expiresOn, today)) done.add(r.nodeId);
  return NODES.map((n) => n.nodeId).filter((id) => done.has(id));
}

/** Active questions for one quiz, in question order. */
export function activeQuestions(all: readonly QuizQuestion[], quizId: QuizId): QuizQuestion[] {
  return all
    .filter((q) => q.quizId === quizId && q.isActive)
    .sort((a, b) => a.questionNumber - b.questionNumber);
}

export interface OptionShown {
  key: "A" | "B" | "C" | "D";
  text: string;
}

/** What the phone gets: no answer key. */
export interface QuestionShown {
  questionId: string;
  questionNumber: number;
  questionText: string;
  options: OptionShown[];
}

export function optionsOf(q: Pick<QuizQuestion, "optionA" | "optionB" | "optionC" | "optionD">): OptionShown[] {
  const all: OptionShown[] = [
    { key: "A", text: q.optionA },
    { key: "B", text: q.optionB },
    { key: "C", text: q.optionC },
    { key: "D", text: q.optionD },
  ];
  return all.filter((o) => o.text.trim() !== "");
}

export function questionShown(q: QuizQuestion): QuestionShown {
  return {
    questionId: q.questionId,
    questionNumber: q.questionNumber,
    questionText: q.questionText,
    options: optionsOf(q),
  };
}

// ---- Stop checks, in the approved order ----

export type StopCode =
  | "RETIRED_CODE"
  | "BAD_CODE"
  | "QUIZ_OFF"
  | "TAG_NOT_CLAIMED"
  | "TAG_OUT_OF_SERVICE"
  | "NO_SESSION"
  | "NODE_DONE"
  | "EARLIER_NODE_OPEN";

export interface Stop {
  code: StopCode;
  title: string;
  message: string;
}

export interface QuizCheckInput {
  legacyTrn: boolean;
  quizId: QuizId | null;
  quiz: Quiz | null;
  activeQuestionCount: number;
  tagId: string;
  tag: { exists: boolean; status: string };
  siteName: string;
  hasOpenSession: boolean;
  /** Records for this QEW: node and dates. */
  records: readonly { nodeId: NodeId; completedOn: string; expiresOn: string | null }[];
  today: string;
}

/** First failing check, or null when the trainee may take the quiz. */
export function quizStop(i: QuizCheckInput): Stop | null {
  if (i.legacyTrn) {
    return { code: "RETIRED_CODE", title: "This code is retired", message: "Scan the quiz code on the slide." };
  }
  if (!i.quizId) {
    return { code: "BAD_CODE", title: "Quiz not found", message: "Scan the quiz code on the slide." };
  }
  if (!i.quiz || i.activeQuestionCount === 0 || i.quiz.isActive === false) {
    return { code: "QUIZ_OFF", title: "This quiz is not available", message: "See your instructor." };
  }
  if (!i.tag.exists) {
    return {
      code: "TAG_NOT_CLAIMED",
      title: "Claim your tag first",
      message: `Tag ${i.tagId} isn't claimed yet. Claim it with your instructor's session code, then scan again.`,
    };
  }
  if (i.tag.status !== "Active") {
    return { code: "TAG_OUT_OF_SERVICE", title: "Tag out of service", message: `Tag ${i.tagId} is out of service. See your instructor.` };
  }
  if (!i.hasOpenSession) {
    return {
      code: "NO_SESSION",
      title: `No class open at ${i.siteName}`,
      message: "Quizzes need an open session. Ask your instructor to start one.",
    };
  }
  const nodeId = i.quiz.nodeId;
  const node = nodeById(nodeId);
  const done = currentNodes(i.records, i.today);
  if (done.includes(nodeId)) {
    const latest = i.records
      .filter((r) => r.nodeId === nodeId && isCurrentOn(r.expiresOn, i.today))
      .sort((a, b) => b.completedOn.localeCompare(a.completedOn))[0];
    return {
      code: "NODE_DONE",
      title: `${nodeLabel(nodeId)} already complete`,
      message: latest
        ? `Completed ${usDate(latest.completedOn)}. ${latest.expiresOn ? `Expires ${usDate(latest.expiresOn)}.` : "Does not expire."} No retake needed.`
        : `${node.nodeName} is complete. No retake needed.`,
    };
  }
  const next = nextOpenNode(done);
  if (next !== nodeId && next !== null) {
    return {
      code: "EARLIER_NODE_OPEN",
      title: `Finish ${nodeLabel(next)} first`,
      message: `${i.quiz.quizName} opens after ${nodeLabel(next)} is complete. Nodes go in order.`,
    };
  }
  return null;
}

/** 2029-10-04 reads 10/04/2029. */
export function usDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${m}/${d}/${y}` : iso;
}

export function nodeLabel(nodeId: NodeId): string {
  return `Node ${nodeId.slice(4)}`;
}

// ---- Grading ----

export interface Graded {
  answers: AnswerAsSeen[];
  questionsCorrect: number;
  questionsTotal: number;
  scorePct: number;
  isPassed: boolean;
  /** Missed question ID to right answer. Kept on the server on a fail. */
  missedKey: Record<string, string>;
}

/**
 * Grades picks against the active questions. Every active question needs a pick.
 * On a fail, the right answer on each missed question is left blank.
 */
export function grade(
  questions: readonly QuizQuestion[],
  picks: Readonly<Record<string, string>>,
  passMarkPct: number,
): Graded | { problem: string } {
  if (questions.length === 0) return { problem: "This quiz has no active questions." };
  const asked = new Set(questions.map((q) => q.questionId));
  for (const id of Object.keys(picks)) {
    if (!asked.has(id)) return { problem: "The quiz changed while you were taking it. Start again." };
  }
  for (const q of questions) {
    const p = picks[q.questionId];
    if (!p || !optionsOf(q).some((o) => o.key === p)) return { problem: `Question ${q.questionNumber} has no answer.` };
  }
  let correct = 0;
  const missedKey: Record<string, string> = {};
  const answers: AnswerAsSeen[] = questions.map((q) => {
    const picked = picks[q.questionId] ?? "";
    const isCorrect = picked === q.correctAnswer;
    if (isCorrect) correct++;
    else missedKey[q.questionId] = q.correctAnswer;
    return { questionId: q.questionId, questionText: q.questionText, picked, correctAnswer: q.correctAnswer, isCorrect };
  });
  const passed = isPassed(correct, questions.length, passMarkPct);
  if (!passed) {
    for (const a of answers) if (!a.isCorrect) a.correctAnswer = "";
  }
  return {
    answers,
    questionsCorrect: correct,
    questionsTotal: questions.length,
    scorePct: scorePct(correct, questions.length),
    isPassed: passed,
    missedKey,
  };
}

// ---- Correction ----

export interface CorrectionState {
  isPassed: boolean;
  answers: readonly AnswerAsSeen[];
  corrections: Readonly<Record<string, readonly string[]>>;
  correctedQuestionIds: readonly string[];
  correctedAt: string | null;
}

export type CorrectionResult =
  | { problem: string; code: "NOT_FAILED" | "NOT_MISSED" | "ALREADY_CORRECTED" | "SESSION_ENDED" | "BAD_PICK" }
  | {
      right: boolean;
      corrections: Record<string, string[]>;
      correctedQuestionIds: string[];
      remaining: number;
      finished: boolean;
    };

/** One Check answer tap. Returns right or wrong, never the answer. */
export function checkPick(
  state: CorrectionState,
  questionId: string,
  pick: string,
  key: Readonly<Record<string, string>>,
  sessionOpen: boolean,
): CorrectionResult {
  if (state.isPassed || state.correctedAt) {
    return { code: "NOT_FAILED", problem: "This attempt needs no correction." };
  }
  if (!sessionOpen) {
    return { code: "SESSION_ENDED", problem: "Your class has ended. See your instructor." };
  }
  const missed = state.answers.filter((a) => !a.isCorrect).map((a) => a.questionId);
  if (!missed.includes(questionId) || !key[questionId]) {
    return { code: "NOT_MISSED", problem: "That question was not missed." };
  }
  if (state.correctedQuestionIds.includes(questionId)) {
    return { code: "ALREADY_CORRECTED", problem: "That question is already corrected." };
  }
  if (!/^[A-D]$/.test(pick)) return { code: "BAD_PICK", problem: "Pick an answer." };

  const corrections: Record<string, string[]> = {};
  for (const [id, picks] of Object.entries(state.corrections)) corrections[id] = [...picks];
  corrections[questionId] = [...(corrections[questionId] ?? []), pick];

  const right = pick === key[questionId];
  const correctedQuestionIds = right ? [...state.correctedQuestionIds, questionId] : [...state.correctedQuestionIds];
  const remaining = missed.filter((id) => !correctedQuestionIds.includes(id)).length;
  return { right, corrections, correctedQuestionIds, remaining, finished: remaining === 0 };
}

/** Answers with the right answers filled back in once the correction finishes. */
export function answersWithKey(answers: readonly AnswerAsSeen[], key: Readonly<Record<string, string>>): AnswerAsSeen[] {
  return answers.map((a) => (a.isCorrect || a.correctAnswer ? { ...a } : { ...a, correctAnswer: key[a.questionId] ?? "" }));
}

// ---- Sessions ----

export const REOPEN_REASONS = ["Accidental close", "Multi-day training", "Field issue", "Other"] as const;
export type ReopenReason = (typeof REOPEN_REASONS)[number];

export interface FailedInSession {
  attemptId: string;
  name: string;
  quizId: QuizId;
  scorePct: number;
  correctionFinished: boolean;
}

/** End Session needs the review tick when anyone in the session failed. */
export function endSessionProblem(failed: readonly FailedInSession[], reviewConfirmed: boolean): string | null {
  if (failed.length > 0 && !reviewConfirmed) {
    return "Review missed-question feedback with the trainees listed, then tick the box.";
  }
  return null;
}

export function reopenProblem(reason: string): string | null {
  return (REOPEN_REASONS as readonly string[]).includes(reason) ? null : "Pick a reason.";
}

// ---- Quiz Bank ----

export interface QuestionInput {
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: string;
  slideRef: string;
}

/** True and False when C and D are both blank. */
export function questionTypeOf(q: Pick<QuestionInput, "optionC" | "optionD">): "MC" | "TF" {
  return q.optionC.trim() === "" && q.optionD.trim() === "" ? "TF" : "MC";
}

export function questionProblem(q: QuestionInput): string | null {
  if (!q.questionText.trim()) return "Enter the question.";
  if (!q.optionA.trim() || !q.optionB.trim()) return "Enter options A and B.";
  const c = q.optionC.trim() !== "";
  const d = q.optionD.trim() !== "";
  if (c !== d) return "Fill in both C and D, or leave both blank for True and False.";
  const keys = optionsOf(q).map((o) => o.key);
  if (!keys.includes(q.correctAnswer as OptionShown["key"])) return "Pick the correct answer from the options.";
  if (!q.slideRef.trim()) return "Enter the slide reference.";
  return null;
}

/** Next ID and number in a quiz. Quiz 2 with q2-01 to q2-15 gives q2-16, number 16. */
export function nextQuestionId(
  quizId: QuizId,
  existing: readonly { questionId: string; questionNumber: number }[],
): { questionId: string; questionNumber: number } {
  const n = quizId.slice(4);
  let max = 0;
  for (const q of existing) {
    const m = new RegExp(`^q${n}-(\\d+)$`).exec(q.questionId);
    if (m?.[1]) max = Math.max(max, Number(m[1]));
    max = Math.max(max, q.questionNumber);
  }
  const next = max + 1;
  return { questionId: `q${n}-${String(next).padStart(2, "0")}`, questionNumber: next };
}

export function passMarkProblem(pct: number): string | null {
  return Number.isInteger(pct) && pct >= 1 && pct <= 100 ? null : "Pass mark is a whole percent, 1 to 100.";
}
